import { environment } from '../../environments/environment';

/**
 * The two ORIGIN-PARSED scopes that replace the one string-prefix predicate this file used to hold
 * (BRN-2325, design L2-2; L3 S6, S7, S17).
 *
 * BRN-2148 defined a single predicate privately inside `BearerInterceptor` to bound WHERE the
 * session bearer is attached, and BRN-2208 shared it so `HttpErrorInterceptor` could bound WHICH
 * 401s may change session state to the SAME scope. That "one definition" property was right while
 * every credentialled origin was one this app controls. BRN-2325 sends the bearer to the payments
 * API — an origin brain does not control — and at that moment the two questions genuinely diverge:
 *
 * - the SESSION AUTHORITY scope, [isSessionAuthorityUrl] — brain's own API and brain-iam ONLY. A 401
 *   from here says something about the session; a 401 from anywhere else says nothing about it.
 * - the SESSION CREDENTIAL scope, [isSessionCredentialUrl] — the authority scope PLUS the payments
 *   API origin the tenant config hands down after sign-in. This is where the bearer is attached.
 *
 * Both compare the request's ORIGIN exactly against a configured origin, never a string prefix, so
 * the weakness the previous header recorded (`https://<api-host>.evil.example/x` satisfied a prefix
 * test) is closed. Brain's own API additionally keeps its PATH prefix (`/api`): the bearer was never
 * attached to a same-origin request outside `/api`, and origin parsing must not widen that.
 *
 * S7: every parse is `new URL(url, window.location.origin)`. `environment.prod.ts` ships
 * `apiBaseUrl: '/api'` — a RELATIVE string — so a bare `new URL(url)` would throw on every one of
 * brain's own first-party calls and fail either open (no bearer anywhere) or closed (no logout
 * anywhere), depending on how the throw was caught.
 *
 * S17: "public" is excluded as a path SEGMENT, never as a substring, so `/api/publications` is
 * protected and `/api/public/x` is not. Public routes carry no bearer and their 401s say nothing
 * about the session, so the exclusion applies to BOTH session scopes deliberately.
 *
 * A third, wider reading of the same two origins is [isFirstPartyUrl] — the authority scope WITH
 * its public routes — for the one header that must join brain-iam's public sign-in, MFA and OTP
 * routes: the correlation id (`CorrelationIdInterceptor`, L6 A-17). [hasPathSegment] is the general
 * form of the S17 rule, so a route carve-out is never a substring test either (L6 cr-A5).
 *
 * The payments origin is NOT read here: it arrives with the tenant config AFTER boot, while
 * `environment.*` is populated pre-auth, so the credential predicate takes it as an argument and
 * `BearerInterceptor` supplies it from the store (`PaymentsApiOriginService`). Keeping these
 * functions pure is what makes each scope testable as a table of urls.
 */

const PUBLIC_PATH_SEGMENT = 'public';

/** A configured scope: an exact origin plus the path prefix under which it is trusted. */
interface UrlScope {
  origin: string;
  pathPrefix: string;
}

const parse = (url: string): URL | null => {
  const trimmed = url?.trim();

  if (!trimmed) {
    return null;
  }

  try {
    return new URL(trimmed, window.location.origin);
  } catch {
    return null;
  }
};

/**
 * The origin of `url`, resolved against the document origin when `url` is relative (S7), or `null`
 * when the value is not a url at all. Never throws.
 */
export const originOf = (url: string): string | null => {
  const parsed = parse(url);

  if (!parsed || parsed.origin === 'null') {
    return null;
  }

  return parsed.origin;
};

/**
 * Whether `url`'s PATH carries `segment` as a WHOLE segment — never a substring, and never the
 * query or the fragment, which a caller controls.
 */
export const hasPathSegment = (url: string, segment: string): boolean => {
  const parsed = parse(url);

  return parsed !== null && parsed.pathname.split('/').includes(segment);
};

/** Whether `url`'s PATH carries a `public` segment (S17): `/api/public/x` yes, `/api/publications` no. */
export const hasPublicPathSegment = (url: string): boolean => hasPathSegment(url, PUBLIC_PATH_SEGMENT);

const scopeOf = (configured: string): UrlScope | null => {
  const parsed = parse(configured);

  if (!parsed || parsed.origin === 'null') {
    return null;
  }

  return { origin: parsed.origin, pathPrefix: parsed.pathname.replace(/\/+$/, '') };
};

const isWithin = (url: string, scope: UrlScope): boolean => {
  const parsed = parse(url);

  if (!parsed || parsed.origin !== scope.origin) {
    return false;
  }

  return scope.pathPrefix === '' || parsed.pathname === scope.pathPrefix || parsed.pathname.startsWith(`${scope.pathPrefix}/`);
};

const sessionAuthorityScopes = (): UrlScope[] =>
  [environment.apiBaseUrl, environment.baseIamUrl].map(scopeOf).filter((scope): scope is UrlScope => scope !== null);

/**
 * Whether `url` is addressed to one of this app's OWN backends — brain's API under its configured
 * path, or brain-iam — public routes included. The scope of the correlation id: the sign-in, MFA
 * and OTP attempts it exists to join are brain-iam's PUBLIC routes. Never the payments origin —
 * payments' customer-route CORS allowlist admits no such header, so a request carrying one would
 * fail its preflight — and never a third party.
 */
export const isFirstPartyUrl = (url: string): boolean => sessionAuthorityScopes().some(scope => isWithin(url, scope));

/**
 * Whether a 401 from `url` speaks for the session: the first-party scope MINUS its public routes.
 * Brain's own API (under its configured path) and brain-iam only; never the payments origin, never
 * a third party, never a public route (it carries no bearer, so its 401 says nothing).
 */
export const isSessionAuthorityUrl = (url: string): boolean => !hasPublicPathSegment(url) && isFirstPartyUrl(url);

/**
 * Whether the session bearer is attached to `url`: the authority scope, plus the payments API
 * origin the tenant config handed down — compared EXACTLY, so a prefix-similar host, another port
 * or another scheme receives no credential. No payments origin known means no widening at all.
 */
export const isSessionCredentialUrl = (url: string, paymentsApiOrigin: string | null | undefined): boolean => {
  if (isSessionAuthorityUrl(url)) {
    return true;
  }

  const paymentsOrigin = paymentsApiOrigin ? originOf(paymentsApiOrigin) : null;

  if (!paymentsOrigin || hasPublicPathSegment(url)) {
    return false;
  }

  return originOf(url) === paymentsOrigin;
};
